/**
 * Redes Sociais — publicar, contas e marca (B-37 + B-38).
 *
 * A tela faz o que o funcionário fazia à mão: pega a foto, monta a arte
 * on-brand, escreve a legenda e publica em N redes. O catálogo de redes vem do
 * REGISTRY do backend (dado) — o modal de conexão se monta sozinho.
 */
import { useEffect, useMemo, useState } from 'react';

import { Badge, Button } from '@/components/atoms';
import { formatarDataHora } from '@/i18n/formato';
import { ApiError } from '@/services/api';
import {
  type PlatformDef,
  type PublishResultRow,
  type SocialAccount,
  fetchPreview,
  useCreateAccount,
  useDeleteAccount,
  useFormats,
  usePlatformDefs,
  usePublishArticle,
  useSaveTemplate,
  useSocialAccounts,
  useSocialPosts,
  useTemplate,
  useTestAccount,
} from '@/services/social';
import { useAuth } from '@/store/useAuth';

import styles from './Social.module.css';

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e.body as { detail?: string } | null)?.detail;
    if (d) return d;
    return `${e.status} ${e.message}`;
  }
  return e instanceof Error ? e.message : String(e);
}

type Tab = 'publicar' | 'contas' | 'marca';
const TABS: { key: Tab; label: string }[] = [
  { key: 'publicar', label: 'Publicar' },
  { key: 'contas', label: 'Contas' },
  { key: 'marca', label: 'Marca da arte' },
];

export function Social() {
  const token = useAuth((s) => s.token);
  const platformsQ = usePlatformDefs();
  const accountsQ = useSocialAccounts();
  const postsQ = useSocialPosts();
  const formatsQ = useFormats();
  const templateQ = useTemplate();
  const createAccount = useCreateAccount();
  const deleteAccount = useDeleteAccount();
  const testAccount = useTestAccount();
  const saveTemplate = useSaveTemplate();
  const publishArticle = usePublishArticle();

  const [tab, setTab] = useState<Tab>('publicar');
  const [msg, setMsg] = useState<string | null>(null);

  // ── composer
  const [imageUrl, setImageUrl] = useState('');
  const [overlay, setOverlay] = useState('');
  const [caption, setCaption] = useState('');
  const [link, setLink] = useState('');
  const [fmt, setFmt] = useState('feed_portrait');
  const [selected, setSelected] = useState<string[]>([]);
  const [preview, setPreview] = useState<{ url: string; used: string; truncated: boolean } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [results, setResults] = useState<PublishResultRow[] | null>(null);

  // ── conectar conta
  const [connecting, setConnecting] = useState<PlatformDef | null>(null);
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [handle, setHandle] = useState('');
  const [testResult, setTestResult] = useState<Record<string, string>>({});

  // ── marca
  // cores da ARTE gerada para as redes (dado do template do cliente, nao da
  // interface): ficam fixas de proposito e nao acompanham claro/escuro/black
  const [tpl, setTpl] = useState({
    brand_name: '', accent_hex: '#0a6c4a', text_hex: '#ffffff',
    position: 'bottom' as 'bottom' | 'top', scrim: true, headline_max_chars: 90,
  });

  const accounts = accountsQ.data ?? [];
  const platforms = platformsQ.data ?? [];
  const budget = templateQ.data?.headline_budget ?? 90;

  useEffect(() => {
    const t = templateQ.data?.template;
    if (t) {
      setTpl({
        brand_name: t.brand_name, accent_hex: t.accent_hex, text_hex: t.text_hex,
        position: t.position, scrim: t.scrim, headline_max_chars: t.headline_max_chars,
      });
    }
  }, [templateQ.data]);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  const platformOf = (a: SocialAccount) => platforms.find((p) => p.key === a.platform);

  /** Limite EFETIVO = a rede mais restritiva entre as selecionadas. */
  const limiteCaption = useMemo(() => {
    const lims = selected
      .map((id) => accounts.find((a) => a.id === id))
      .map((a) => (a ? platformOf(a)?.max_chars ?? 280 : 280));
    return lims.length ? Math.min(...lims) : 280;
  }, [selected, accounts, platforms]);

  const textoFinal = link.trim() ? `${caption} ${link.trim()}`.trim() : caption;
  const excedeu = textoFinal.length > limiteCaption;

  /** Tudo que impede publicar — mostrado ANTES, não como erro depois. */
  const problemas = useMemo(() => {
    const p: string[] = [];
    if (!caption.trim()) p.push('Escreva a legenda do post.');
    if (selected.length === 0) p.push('Escolha pelo menos uma conta.');
    const semCred = selected
      .map((id) => accounts.find((a) => a.id === id))
      .filter((a) => a && !a.has_credentials);
    if (semCred.length) p.push(`Conta sem credencial: ${semCred.map((a) => a?.handle).join(', ')}.`);
    const inativa = selected
      .map((id) => accounts.find((a) => a.id === id))
      .filter((a) => a && !a.active);
    if (inativa.length) p.push(`Conta inativa: ${inativa.map((a) => a?.handle).join(', ')}.`);
    if (excedeu) p.push(`A legenda passa do limite da rede mais restritiva (${limiteCaption}).`);
    if (imageUrl.trim() && !overlay.trim()) p.push('Com imagem, escreva a headline da arte.');
    return p;
  }, [caption, selected, accounts, excedeu, limiteCaption, imageUrl, overlay]);

  if (accountsQ.isError) {
    return (
      <section className={styles.root}>
        <h1 className={styles.title}>Redes Sociais</h1>
        <p className={styles.muted}>
          Recurso não incluído no plano desta conta (<code>social_publisher</code>).
        </p>
      </section>
    );
  }

  async function doPreview() {
    if (!imageUrl.trim() || !overlay.trim()) {
      setMsg('Para ver a arte, informe a imagem e a headline.');
      return;
    }
    setMsg(null);
    setPreviewing(true);
    try {
      const r = await fetchPreview(
        { headline_overlay: overlay, image_url: imageUrl.trim(), fmt }, token,
      );
      setPreview({ url: r.blobUrl, used: r.used, truncated: r.truncated });
    } catch (err) {
      setMsg(`Preview: ${describeError(err)}`);
    } finally {
      setPreviewing(false);
    }
  }

  async function doPublish() {
    if (problemas.length) return;
    setMsg(null);
    setResults(null);
    try {
      const out = await publishArticle.mutateAsync({
        account_ids: selected,
        headline_overlay: overlay.trim() || caption.trim().slice(0, budget),
        caption: caption.trim(),
        image_url: imageUrl.trim() || undefined,
        link: link.trim() || undefined,
        fmt,
      });
      setResults(out.results);
      if (out.ok === out.total) {
        setCaption(''); setOverlay(''); setImageUrl(''); setLink('');
        setPreview(null);
      }
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function submitConnect(e: React.FormEvent) {
    e.preventDefault();
    if (!connecting) return;
    setMsg(null);
    try {
      await createAccount.mutateAsync({
        platform: connecting.key,
        handle: (handle || creds.identifier || '').trim(),
        credentials: creds,
      });
      setConnecting(null);
      setCreds({});
      setHandle('');
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function doTest(a: SocialAccount) {
    try {
      const r = await testAccount.mutateAsync(a.id);
      setTestResult((p) => ({ ...p, [a.id]: r.ok ? `${r.detail}` : `${r.detail}` }));
    } catch (err) {
      setTestResult((p) => ({ ...p, [a.id]: `${describeError(err)}` }));
    }
  }

  async function doSaveTemplate(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      await saveTemplate.mutateAsync(tpl);
      setMsg('Marca salva — as próximas artes já saem assim.');
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function onLogoFile(file: File) {
    const b64 = await new Promise<string>((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(String(fr.result).split(',')[1] ?? '');
      fr.onerror = rej;
      fr.readAsDataURL(file);
    });
    try {
      await saveTemplate.mutateAsync({ ...tpl, logo_b64: b64 });
      setMsg('Logo enviado.');
    } catch (err) {
      setMsg(`Erro no logo: ${describeError(err)}`);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Redes Sociais</h1>
          <p className={styles.subtitle}>
            Foto + texto → arte on-brand → publicado. Sem passar por editor de arte
            nem por agendador externo.
          </p>
        </div>
      </header>

      <nav className={styles.tabs}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {msg && <p className={styles.flash}>{msg}</p>}

      {/* ─── PUBLICAR ──────────────────────────────────────────────── */}
      {tab === 'publicar' && (
        <div className={styles.composerGrid}>
          <div className={styles.col}>
            <label className={styles.field}>
              <span>Imagem (URL) — opcional</span>
              <input
                value={imageUrl}
                onChange={(e) => { setImageUrl(e.target.value); setPreview(null); }}
                placeholder="https://…/foto.jpg"
              />
            </label>

            <label className={styles.field}>
              <span>
                Headline da ARTE{' '}
                <em className={overlay.length > budget ? styles.over : styles.mutedSmall}>
                  {overlay.length}/{budget}
                </em>
              </span>
              <input
                value={overlay}
                onChange={(e) => { setOverlay(e.target.value); setPreview(null); }}
                placeholder="Frase curta que vai QUEIMADA na imagem"
              />
            </label>

            <div className={styles.fieldRow}>
              <label className={styles.field}>
                <span>Formato</span>
                <select value={fmt} onChange={(e) => { setFmt(e.target.value); setPreview(null); }}>
                  {(formatsQ.data?.formats ?? []).map((f) => (
                    <option key={f.key} value={f.key}>{f.key} · {f.ratio}</option>
                  ))}
                </select>
              </label>
              <div className={styles.field}>
                <span>&nbsp;</span>
                <Button variant="secondary" loading={previewing} onClick={doPreview}>
                  Ver a arte
                </Button>
              </div>
            </div>

            <label className={styles.field}>
              <span>
                Legenda do post{' '}
                <em className={excedeu ? styles.over : styles.mutedSmall}>
                  {textoFinal.length}/{limiteCaption}
                </em>
              </span>
              <textarea
                className={styles.textarea}
                rows={5}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="O texto que acompanha o post…"
              />
            </label>

            <label className={styles.field}>
              <span>Link (opcional — preservado no corte)</span>
              <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" />
            </label>

            <div className={styles.field}>
              <span>Publicar em</span>
              <div className={styles.accountPicker}>
                {accounts.length === 0 && (
                  <span className={styles.mutedSmall}>
                    Nenhuma conta conectada — vá em “Contas”.
                  </span>
                )}
                {accounts.map((a) => (
                  <label key={a.id} className={styles.accountChip}>
                    <input
                      type="checkbox"
                      checked={selected.includes(a.id)}
                      onChange={(e) =>
                        setSelected((s) =>
                          e.target.checked ? [...s, a.id] : s.filter((i) => i !== a.id),
                        )
                      }
                    />
                    <strong>{platformOf(a)?.label ?? a.platform}</strong>
                    <span className={styles.mutedSmall}>{a.handle}</span>
                    {!a.has_credentials && <Badge tone="crit" size="xs">sem credencial</Badge>}
                  </label>
                ))}
              </div>
            </div>

            {problemas.length > 0 && (
              <ul className={styles.problemas}>
                {problemas.map((p) => <li key={p}>{p}</li>)}
              </ul>
            )}

            <Button
              onClick={doPublish}
              loading={publishArticle.isPending}
              disabled={problemas.length > 0}
            >
              Publicar em {selected.length} conta(s)
            </Button>

            {results && (
              <div className={styles.results}>
                {results.map((r) => (
                  <p key={r.account_id} className={r.ok ? styles.okLine : styles.errLine}>
                    {r.ok ? '' : ''} {r.platform ?? ''}{' '}
                    {r.ok
                      ? <a href={r.url ?? '#'} target="_blank" rel="noreferrer">ver publicação ↗</a>
                      : r.error}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div className={styles.col}>
            <span className={styles.previewLabel}>Prévia da arte</span>
            <div className={styles.previewBox}>
              {preview ? (
                <img className={styles.previewImg} src={preview.url} alt="Prévia da peça" />
              ) : (
                <span className={styles.muted}>
                  Informe imagem + headline e clique em “Ver a arte”.
                </span>
              )}
            </div>
            {preview?.truncated && (
              <p className={styles.over}>
                A headline foi cortada para caber ({budget} caracteres). Encurte para
                não sair com reticências.
              </p>
            )}
          </div>
        </div>
      )}

      {/* ─── CONTAS ────────────────────────────────────────────────── */}
      {tab === 'contas' && (
        <div>
          <div className={styles.connectRow}>
            {platforms.map((p) => (
              <Button
                key={p.key}
                size="sm"
                variant="secondary"
                disabled={!p.available}
                title={p.available ? p.hint : 'Conector em breve'}
                onClick={() => { setConnecting(p); setCreds({}); setHandle(''); setMsg(null); }}
              >
                {p.available ? `+ ${p.label}` : `${p.label} (em breve)`}
              </Button>
            ))}
          </div>

          <table className={styles.table}>
            <thead>
              <tr><th>Rede</th><th>Conta</th><th>Estado</th><th>Ações</th></tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id}>
                  <td><Badge tone="info" size="xs">{platformOf(a)?.label ?? a.platform}</Badge></td>
                  <td>
                    <strong>{a.handle}</strong>
                    {testResult[a.id] && (
                      <><br /><span className={styles.mutedSmall}>{testResult[a.id]}</span></>
                    )}
                  </td>
                  <td>
                    <Badge tone={a.active && a.has_credentials ? 'ok' : 'crit'} size="xs">
                      {a.has_credentials ? (a.active ? 'ativa' : 'inativa') : 'sem credencial'}
                    </Badge>
                  </td>
                  <td className={styles.actions}>
                    <Button size="sm" variant="secondary" loading={testAccount.isPending} onClick={() => doTest(a)}>
                      Testar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (window.confirm(`Remover ${a.handle}?`)) deleteAccount.mutate(a.id);
                      }}
                    >
                      Remover
                    </Button>
                  </td>
                </tr>
              ))}
              {accounts.length === 0 && (
                <tr><td colSpan={4} className={styles.muted}>Nenhuma conta conectada.</td></tr>
              )}
            </tbody>
          </table>

          <h2 className={styles.sectionTitle}>Publicados</h2>
          {/* A espinha de métricas/loop que enriqueceria esta lista não existe
              ainda (análise de lacunas) — dizer o alcance real evita procurar
              posts que nunca vão aparecer aqui. */}
          <p className={styles.muted}>
            Lista apenas o que foi publicado por aqui — métricas e histórico
            completo chegam com o coletor (em construção).
          </p>
          <table className={styles.table}>
            <thead><tr><th>Rede</th><th>Texto</th><th>Quando</th><th /></tr></thead>
            <tbody>
              {(postsQ.data ?? []).map((p) => (
                <tr key={p.id}>
                  <td><Badge tone="neutral" size="xs">{p.platform}</Badge></td>
                  <td className={styles.caption}>{p.caption}</td>
                  <td className={styles.mutedSmall}>
                    {p.created_at ? formatarDataHora(p.created_at) : '—'}
                  </td>
                  <td>{p.url && <a href={p.url} target="_blank" rel="noreferrer">abrir ↗</a>}</td>
                </tr>
              ))}
              {(postsQ.data ?? []).length === 0 && (
                <tr><td colSpan={4} className={styles.muted}>Nada publicado ainda.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── MARCA ─────────────────────────────────────────────────── */}
      {tab === 'marca' && (
        <form className={styles.composer} onSubmit={doSaveTemplate}>
          {/* A PRIMEIRA metade da promessa FLUI: o template gravado aqui vira
              overlay_for → build_asset → enforce_overlay_limit e sai queimado no
              PNG que vai para a rede. A SEGUNDA não: content_engine._write() tem
              o parâmetro `overlay_budget` (content_engine.py:909), mas nenhum dos
              dois chamadores (content_engine.py:1574 e prompt_flows.py:699) o
              passa — o bloco que pediria "headline_overlay" à IA nunca entra no
              prompt. Rotulamos só o que falta. */}
          <p className={styles.muted}>
            Configure uma vez: toda arte sai assim, sem abrir editor. O limite de
            caracteres vale para a arte — o render corta a headline nesse teto.
            Ainda não governa a IA: a geração de texto não recebe esse número, então
            a headline sai do que você digita em “Publicar” (em construção).
          </p>
          <div className={styles.fieldRow}>
            <label className={styles.field}>
              <span>Nome da marca</span>
              <input
                value={tpl.brand_name}
                onChange={(e) => setTpl({ ...tpl, brand_name: e.target.value })}
                placeholder="NC NEWS"
              />
            </label>
            <label className={styles.field}>
              <span>Logo (PNG)</span>
              <input
                type="file"
                accept="image/png"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onLogoFile(f); }}
              />
            </label>
          </div>
          <div className={styles.fieldRow}>
            <label className={styles.field}>
              <span>Cor de realce</span>
              <input type="color" value={tpl.accent_hex}
                onChange={(e) => setTpl({ ...tpl, accent_hex: e.target.value })} />
            </label>
            <label className={styles.field}>
              <span>Cor do texto</span>
              <input type="color" value={tpl.text_hex}
                onChange={(e) => setTpl({ ...tpl, text_hex: e.target.value })} />
            </label>
            <label className={styles.field}>
              <span>Posição</span>
              <select value={tpl.position}
                onChange={(e) => setTpl({ ...tpl, position: e.target.value as 'bottom' | 'top' })}>
                <option value="bottom">rodapé</option>
                <option value="top">topo</option>
              </select>
            </label>
          </div>
          <div className={styles.fieldRow}>
            <label className={styles.field}>
              <span>Limite da headline na arte ({tpl.headline_max_chars})</span>
              <input
                type="range" min={20} max={200} value={tpl.headline_max_chars}
                onChange={(e) => setTpl({ ...tpl, headline_max_chars: Number(e.target.value) })}
              />
              <small className={styles.mutedSmall}>
                O corte da arte respeita este teto. A IA ainda não recebe este
                número (em construção).
              </small>
            </label>
            <label className={styles.checkField}>
              <input type="checkbox" checked={tpl.scrim}
                onChange={(e) => setTpl({ ...tpl, scrim: e.target.checked })} />
              <span>Escurecer o fundo atrás do texto (legibilidade)</span>
            </label>
          </div>
          <div className={styles.modalActions}>
            <Button type="submit" loading={saveTemplate.isPending}>Salvar marca</Button>
          </div>
        </form>
      )}

      {/* ─── modal de conexão (montado a partir do REGISTRY) ───────── */}
      {connecting && (
        <div className={styles.modalBackdrop} onClick={() => setConnecting(null)}>
          <form className={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={submitConnect}>
            <h2 className={styles.modalTitle}>Conectar {connecting.label}</h2>
            {connecting.hint && <p className={styles.muted}>{connecting.hint}</p>}
            {connecting.doc_url && (
              <a className={styles.mutedSmall} href={connecting.doc_url} target="_blank" rel="noreferrer">
                documentação ↗
              </a>
            )}
            <label className={styles.field}>
              <span>Identificação da conta (exibição)</span>
              <input value={handle} onChange={(e) => setHandle(e.target.value)}
                placeholder="@marca" required />
            </label>
            {connecting.fields.map((f) => (
              <label key={f.key} className={styles.field}>
                <span>{f.label}</span>
                <input
                  type={f.type === 'password' ? 'password' : 'text'}
                  value={creds[f.key] ?? ''}
                  onChange={(e) => setCreds((c) => ({ ...c, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  autoComplete="off"
                  required={f.required}
                />
                {f.hint && <span className={styles.mutedSmall}>{f.hint}</span>}
              </label>
            ))}
            <p className={styles.mutedSmall}>
              A credencial é testada contra a rede antes de gravar — se não autenticar,
              não entra. Fica cifrada no cofre e nunca volta para a tela.
            </p>
            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={() => setConnecting(null)}>Cancelar</Button>
              <Button type="submit" loading={createAccount.isPending}>Conectar</Button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
